import React from "react";
import Header from "./header";
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
  Link,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccidentAssessmentForm from "../components/forms/AccidentAssessmentForm";
import Footer from "./footer";
import { useParams } from "react-router-dom";
import { extractNumber } from "../utils/helper";
import TextBox from "../components/textbox";
import { useTranslation } from "../components/hooks/useTranslation";
import { CountdownTimer } from "../components/countdown/CountdownTimer";

const HomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { funnelSource } = useParams<{ funnelSource?: string }>();

  const { t } = useTranslation();

  const sourceNumber = extractNumber(funnelSource);

  // Trust badges data
  const trustBadges = [
    { name: "Google", rating: "5.0" },
    { name: "Yelp", rating: "5.0" },
    { name: "Trustpilot", rating: "4.9" },
  ];

  // Benefits list
  const benefits = [
    "Experienced attorneys in your area",
    "No upfront costs or hidden fees",
    "Free case consultation",
  ];

  return (
    <>
      <Header />
      <Box
        sx={{
          backgroundColor: "#E8F3F8",
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          padding: { xs: "1.5rem 0", md: "3rem 0" },
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            left: "5%",
            width: "300px",
            height: "300px",
            background:
              "radial-gradient(circle, rgba(255,107,53,0.1) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "20%",
            right: "10%",
            width: "400px",
            height: "400px",
            background:
              "radial-gradient(circle, rgba(5,177,235,0.08) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Main Heading */}
          {sourceNumber === 2 && (
            <>
              <Typography
                variant={isMobile ? "h5" : "h3"}
                align="center"
                sx={{
                  color: "black",
                  fontWeight: 700,
                  mt: 0,
                  mb: 2,
                  fontSize: { xs: "19px", sm: "1.8rem", md: "3.2rem" },
                  lineHeight: 1.3,
                }}
              >
                {t("homePageYT.firstTitle")}
                <br />
                <Box
                  component="span"
                  sx={{
                    color: "red",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: "-4px",
                      left: 0,
                      width: "100%",
                      height: "3px",
                      // backgroundColor: "red",
                      borderRadius: "2px",
                    },
                  }}
                >
                  {t("homePageYT.secondTitle")}
                </Box>
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "black",
                  mb: isMobile ? 2 : 4,
                  mt: isMobile ? 1 : 2,
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                  fontWeight: 400,
                }}
              >
                {t("homePageYT.firstSubtitle")}{" "}
                <Box component="span" sx={{ color: "red", fontWeight: 600 }}>
                  {t("homePageYT.secondSubtitle")}
                </Box>{" "}
                {t("homePageYT.thirdSubtitle")}
              </Typography>
            </>
          )}

          {sourceNumber !== 2 && (
            <>
              <Typography
                variant={isMobile ? "h5" : "h3"}
                align="center"
                sx={{
                  color: "black",
                  fontWeight: 700,
                  mt: 0,
                  mb: 1,
                  fontSize: { xs: "23px", sm: "1.8rem", md: "3.2rem" },
                  lineHeight: 1.3,
                }}
              >
                {t("homePageFB.firstTitle")}{" "}
                <Box
                  component="span"
                  sx={{
                    color: "red",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: "-4px",
                      left: 0,
                      width: "100%",
                      height: "3px",
                      // backgroundColor: "red",
                      borderRadius: "2px",
                    },
                  }}
                >
                  {t("homePageFB.secondTitle")}
                </Box>{" "}
                {t("homePageFB.thirdTitle")}
              </Typography>

              <Typography
                variant={isMobile ? "h6" : "h4"}
                align="center"
                sx={{
                  color: "black",
                  fontWeight: 700,
                  mb: isMobile ? 2 : 3,
                  fontSize: { xs: "17px", sm: "1.2rem", md: "2rem" },
                }}
              >
                {t("homePageFB.fourthTitle")}
              </Typography>

              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "black",
                  mb: isMobile ? 2 : 4,
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.15rem" },
                  fontWeight: 400,
                }}
              >
                {t("homePageFB.firstSubtitle")}{" "}
                <Box component="span" sx={{ color: "red", fontWeight: 600 }}>
                  {t("homePageFB.secondSubtitle")}
                </Box>{" "}
                {t("homePageFB.thirdSubtitle")}{" "}
                <Box component="span" sx={{ color: "red", fontWeight: 600 }}>
                  {t("homePageFB.fourthSubtitle")}
                </Box>
              </Typography>
            </>
          )}

          {sourceNumber === 1 && (
            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(10px)",
                borderRadius: 3,
                mx: "auto",
                maxWidth: "700px",
                textAlign: "center",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <CountdownTimer
                duration={120}
                isAnswered={false}
                bannerMessage={t("homePageFB.timer")}
                timerSx={{ fontSize: "1.5rem", color: "#fff" }}
                containerSx={{ mb: 4 }}
              />
            </Box>
          )}

          {sourceNumber === 3 && <TextBox />}

          {/* Assessment Form */}
          <Paper
            elevation={0}
            sx={{
              pt: 3,
              pb: 4,
              px: { xs: 2, sm: 4 },
              borderRadius: 4,
              mb: 4,
              width: "100%",
              maxWidth: "700px",
              mx: "auto",
              backgroundColor: "white",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            {sourceNumber !== 6 ? (
              <AccidentAssessmentForm sourceNumber={sourceNumber ?? 0} />
            ) : (
              <AccidentAssessmentForm
                sourceNumber={6}
                skipSteps={["INJURY", "REPRESENTATION", "FAULT"]}
                defaultValues={{
                  physicalInjury: "YES",
                  treatedBy: "YES",
                  hasAttorney: "NO",
                  accidentFault: "NO",
                }}
              />
            )}
          </Paper>

          {/* Free Consultation Text */}
          <Typography
            variant="subtitle1"
            align="center"
            sx={{
              color: "black",
              fontWeight: 500,
              mb: 3,
            }}
          >
            {t("homepage.text")}
          </Typography>

          {/* Trust Badges */}
          {/* <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 4 }}
          >
            {trustBadges.map((badge) => (
              <Box
                key={badge.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(10px)",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Stack direction="row" spacing={0.25}>
                  {[...Array(5)].map((_, i) => (
                    <StarIcon
                      key={i}
                      sx={{ fontSize: "1rem", color: "#fbbf24" }}
                    />
                  ))}
                </Stack>
                <Typography
                  sx={{
                    color: "black",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                  }}
                >
                  {badge.rating} {badge.name}
                </Typography>
              </Box>
            ))}
          </Stack> */}

          {/* Benefits */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 4 }}
          >
            {benefits.map((benefit, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CheckCircleIcon
                  sx={{ color: "#10b981", fontSize: "1.2rem" }}
                />
                <Typography
                  sx={{
                    color: "black",
                    fontSize: "0.95rem",
                  }}
                >
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* TCPA Section */}
      <Container sx={{ mt: 4, mb: 4 }} maxWidth="md">
        <Typography
          sx={{
            mb: 1.5,
            textAlign: "center",
            color: "#64748b",
            fontSize: "0.875rem",
          }}
        >
          {t("homepage.tcpa1")}{" "}
          <Link
            href="https://www.caraccidentcounsel.com/terms-of-service/"
            sx={{ color: "#05b1eb" }}
          >
            {t("homepage.tcpa2")}
          </Link>{" "}
          {t("homepage.tcpa3")}{" "}
          <Link
            href="https://www.caraccidentcounsel.com/network-of-lawyers-and-advocates/"
            sx={{ color: "#05b1eb" }}
          >
            {t("homepage.tcpa4")}
          </Link>{" "}
          {t("homepage.tcpa5")}
        </Typography>
        <Typography
          sx={{
            mb: 1.5,
            textAlign: "center",
            color: "#64748b",
            fontSize: "0.875rem",
          }}
        >
          {t("homepage.tcpa6")}
        </Typography>
        <Typography
          sx={{
            mb: 1.5,
            textAlign: "center",
            color: "#64748b",
            fontSize: "0.875rem",
          }}
        >
          {t("homepage.tcpa7")}
        </Typography>
        <Typography
          sx={{
            mb: 1.5,
            textAlign: "center",
            color: "#64748b",
            fontSize: "0.875rem",
          }}
        >
          {t("homepage.tcpa8")}{" "}
          <Link
            href="https://www.caraccidentcounsel.com/sponsors/"
            sx={{ color: "#05b1eb" }}
          >
            {t("homepage.tcpa9")}
          </Link>{" "}
          {t("homepage.tcpa10")}
        </Typography>
      </Container>
      <Footer />
    </>
  );
};

export default HomePage;
